import React from 'react';
import { processTimeStamp } from "./chartUtils";
import { DateTime } from "luxon";
import { publishMQTTStatus } from "./mqttUtils";

export const createAction = (
    sensor,                 //MacAddress for parent sensor.
    macAddress, 
    actuatorDescription, 
    actuatorType, 
    pwm,
    tracking = '1h',  // Default to 1 hour instead of false
    status = "off",
    lastActive = new Date().toLocaleDateString('en-US', {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
    }),
    timestamp=processTimeStamp(Date.now())
    ) => {
    return {
        sensor: sensor,
        macAddress: macAddress,
        actuatorDescription: actuatorDescription,
        actuatorType: actuatorType,
        pwm: pwm,
        tracking: tracking,
        status: status,
        lastActive: lastActive,
        timestamp:timestamp
    }   
}

const fieldMapping = {
    actuator_description: "actuatorDescription",
    actuator_type: "actuatorType",
    duration: "duration",
    last_active: "lastActive",
    mac_address: "macAddress",
    pwm: "pwm",
    sensor: "sensor",
    sensor_tracking: "sensorTracking",  // ✅ Added sensor_tracking mapping
    status: "status",
    tracking: "tracking",
    timestamp: "timestamp",
    irrigation2_low: "irrigation2Low",  // ✅ Added irrigation2_low mapping
    irrigation2_high: "irrigation2High"  // ✅ Added irrigation2_high mapping
}

export const mapActionsToCamelCase = (data) => {
    if (!data) {
        return [];
    }

    if (Array.isArray(data)) {
        //For each item, map its field.
        return data.map((item) => mapActionsToCamelCase(item));
    }

    // Check if data is already in camelCase format (backend changed to send camelCase)
    const isAlreadyCamelCase = 'actuatorDescription' in data || 'actuatorType' in data || 'macAddress' in data;
    
    if (isAlreadyCamelCase) {
        // Backend is sending camelCase directly, just return as-is
        console.log('[ACTION_MAPPING] Backend sending camelCase, returning data as-is');
        return data;
    }

    // Otherwise, map from snake_case to camelCase (legacy support)
    const mappedData = {};
    for (const [dbField, frontendField] of Object.entries(fieldMapping)) {
        if(dbField in data) {
            //Use frontendField as the new key (camelCase) and store the data from old key.
            mappedData[frontendField] = data[dbField];
        }
    }
    
    console.log('[ACTION_MAPPING] Mapped snake_case to camelCase:', { original: Object.keys(data), mapped: Object.keys(mappedData) });
    return mappedData;
}

export const getSensorType = (actuatorType) => {
    const sensorValueMap = {
        'irrigation': 'distance',
        'irrigation2': 'distance',
        'humidity': 'humidity',
        'temperature': 'temperature',
        'light': 'lux',
        'fan': 'humidity',
        'camera': 'light' // Camera uses light sensor for triggering
    }
    return sensorValueMap[actuatorType];
}

export const getActuatorValue = (actuatorType, card) => {
    if (!card) return null;
    
    const actuatorValueMap = {
        'irrigation': card.mostRecentWater,
        'irrigation2': card.mostRecentWater2,
        'humidity': card.mostRecentHumidity,
        'temperature': card.mostRecentTemp,
        'light': null,
        'fan': null,
        'camera': card.mostRecentLight // Camera shows light level for triggering
    }
    return actuatorValueMap[actuatorType];
}

export const getActuatorUnit = (actuatorType) => {
    const actuatorUnitMap = {
        'irrigation': '"',
        'irrigation2': '"',
        'humidity': '%',
        'temperature': 'F',
        'light': '',
        'fan': '%',
        'camera': 'on/off'
    }
    return actuatorUnitMap[actuatorType];
}

export const getActuatorHigh = (actuatorType, card) => {
    const actuatorHighMap = {
        'irrigation': card?.waterHigh,
        'irrigation2': card?.water2High,
        'humidity': card?.humidityHigh,
        'temperature': card?.temperatureHigh,
        'light': card?.lightHigh,
        'fan': null,
        'camera': card?.lightHigh // Camera uses light thresholds
    }
    return actuatorHighMap[actuatorType];
}

export const getActuatorLow = (actuatorType, card) => {
    const actuatorLowMap = {
        'irrigation': card?.waterLow,
        'irrigation2': card?.water2Low,
        'humidity': card?.humidityLow,
        'temperature': card?.temperatureLow,
        'light': card?.lightLow,
        'fan': null,
        'camera': card?.lightLow // Camera uses light thresholds
    }
    return actuatorLowMap[actuatorType];
}

export const getActuatorColor = (actuatorType) => {
    const actuatorColorMap = {
      temperature: '#FF4C4C',   // Soft red
      humidity: '#4CAF50',      // Fresh green
      light: '#FFD700',         // Golden yellow
      irrigation: '#87CEFA',    // Light blue
      irrigation2: '#4DD0E1',   // Cyan blue
      fan: '#A9A9A9',           // Dark gray
      camera: '#E8E8E8'         // Vibrant white/steel
    };
  
    return actuatorColorMap[actuatorType] || '#CCCCCC'; // default fallback color
}

// Fixed reference date (no DST transition in US) so parsing/display don't depend on "today" or DST.
const LIGHT_THRESHOLD_REFERENCE_DATE = '2024-06-15';

function parseFlexibleTime(timeStr, timezone) {
    // Parse with an explicit date so the result is unambiguous regardless of "today" or DST.
    // This avoids a 1-hour shift when the user's "today" or browser zone crosses DST.
    const withDate = `${LIGHT_THRESHOLD_REFERENCE_DATE} ${timeStr.trim()}`;
    let dt = DateTime.fromFormat(withDate, 'yyyy-MM-dd HH:mm', { zone: timezone });
    if (dt.isValid) return dt;

    dt = DateTime.fromFormat(withDate, 'yyyy-MM-dd hh:mm a', { zone: timezone });
    if (dt.isValid) return dt;

    throw new Error(`Unparseable time format: "${timeStr}". Expected HH:mm or hh:mm a.`);
}

export const calculateLightThresholds = (startTime, endTime, timezone = 'America/Chicago') => {
  const start = parseFlexibleTime(startTime, timezone);
  const end = parseFlexibleTime(endTime, timezone);

  // Local minutes in a single day (0–1439). An earlier end is overnight
  // (11 PM–1 AM is 1380 and 60). Firmware treats high <= low as crossing midnight.
  // Do not add 1440: the clock never reaches 1500, so that encoding turns the light off at midnight.
  const startLocalMinutes = start.hour * 60 + start.minute;
  const endLocalMinutes = end.hour * 60 + end.minute;

  return {
    low: startLocalMinutes.toString(),
    high: endLocalMinutes.toString()
  };
};

export const convertThresholdsToTimes = (low, high, timezone = 'America/Chicago') => {
    if (!low || !high) return { startTime: "00:00", endTime: "23:59" };

    const startMinutes = parseInt(low, 10);
    const endMinutes = parseInt(high, 10);

    // Validate both inputs are numbers and not NaN
    const isValid = !isNaN(startMinutes) && !isNaN(endMinutes);
    if (!isValid) {
        return { startTime: "00:00", endTime: "23:59" };
    }

    // Older saves stored an overnight end as minutes + 1440 (1 AM → 1500).
    // Fold that back into the same day so the picker still shows 1:00 AM.
    const startOfDay = ((startMinutes % 1440) + 1440) % 1440;
    const endOfDay = ((endMinutes % 1440) + 1440) % 1440;

    // Use a fixed reference date so "minutes since midnight" always maps to the same
    // display time regardless of "today" or daylight saving. Avoids 1-hour shift on DST days.
    const base = DateTime.fromISO(`${LIGHT_THRESHOLD_REFERENCE_DATE}T00:00:00`, { zone: timezone });

    const startLocal = base.plus({ minutes: startOfDay });
    const endLocal = base.plus({ minutes: endOfDay });

    const startTime = startLocal.toFormat('hh:mm a');
    const endTime = endLocal.toFormat('hh:mm a');

    return { startTime, endTime };
};  


export const normalizeToTimeInputFormat = (timeString) => {
    if (!timeString) return '';
  
    // Try to parse using 12-hour format first
    let dt = DateTime.fromFormat(timeString, 'hh:mm a');
    if (!dt.isValid) {
      // Fallback to parsing as 24-hour format
      dt = DateTime.fromFormat(timeString, 'HH:mm');
    }
  
    return dt.isValid ? dt.toFormat('HH:mm') : '';
};  


export const getFormattedThreshold = (actuatorType, card, timezone = 'America/Chicago') => {
    let low = getActuatorLow(actuatorType, card);
    let high = getActuatorHigh(actuatorType, card);
    if(actuatorType === "light") {
        const { startTime, endTime } = convertThresholdsToTimes(low, high, timezone);
        // Remove any space before AM/PM (e.g., "08:00 AM" → "08:00AM")
        low = startTime.replace(/\s+(AM|PM)/i, '$1');
        high = endTime.replace(/\s+(AM|PM)/i, '$1');
    }
    return { low, high };
}

/**
 * Turn a publishMQTTStatus / safeguard failure string into copy for the status banner.
 * Input is typically "failed: timeout" or a full sentence already prefixed with "failed: ".
 */
export const formatActionFailureMessage = (result) => {
  if (result == null || result === '') {
    return 'Device did not confirm the update.';
  }
  const raw = String(result);
  const reason = raw.replace(/^failed:\s*/i, '').trim();

  const presets = {
    timeout: 'No success reply from the device within 12s. A presence ping does not confirm this save.',
    'device reported failure': 'Device rejected the command (pairing or payload).',
    'connection error': 'Could not connect to the MQTT broker.',
    'subscription error': 'Could not subscribe for the device response.',
    'publish error': 'Could not publish the command to the device.',
    'missing required data': 'Missing sensor or action pairing.',
    'bounds not configured': 'Thresholds are not configured for this action.',
    'sensor value outside bounds': 'Sensor reading is outside the configured bounds.',
  };

  if (presets[reason]) return presets[reason];
  // Already a full sentence from validation / safeguard
  if (reason.length > 40) return reason;
  return reason || 'Device did not confirm the update.';
};

/**
 * Check if an actuator supports immediate triggers
 * @param {string} actuatorType - The type of actuator
 * @returns {boolean} - Returns true if it supports immediate triggers
 */
export const supportsImmediateTrigger = (actuatorType) => {
  return ["irrigation", "irrigation2", "fan", "camera"].includes(actuatorType);
};

/**
 * Check if an actuator is an irrigation type
 * @param {string} actuatorType - The type of actuator
 * @returns {boolean} - Returns true if it's an irrigation actuator
 */
export const isIrrigationActuator = (actuatorType) => {
  return actuatorType === "irrigation" || actuatorType === "irrigation2";
};

/**
 * Handles the irrigation actuator safeguard logic
 * When immediate watering is selected (2s), it will automatically revert to 1h after the duration parameter
 * 
 * @param {Object} actionCard - The action card object
 * @param {Object} sensorCard - The sensor card object
 * @param {Function} handleSettingChange - Function to change settings (from ActionWrapper)
 * @param {Object} thresholds - The thresholds object for the actuator
 * @param {string} tracking - The selected tracking interval value
 * @param {Function} setMqttStatus - Function to set MQTT status display
 * @param {Object} queryClient - React Query client for updating data
 * @returns {Promise<string|{result: string, trackingReset: boolean}>} - MQTT result (and whether tracking was reset)
 */
export const handleImmediateActionSafeguard = async (
  actionCard,
  sensorCard,
  handleSettingChange,
  thresholds,
  tracking,
  setMqttStatus,
  latestSensorData = null, // Add parameter for latest sensor data
  actionSettings = {} // Add parameter for action settings
) => {
  if (!actionCard?.macAddress || !sensorCard?.macAddress) {
    return { result: "failed: missing required data", trackingReset: false };
  }

  // Helper function to check if tracking is a short duration action
  const isShortDurationAction = (trackingValue) => {
    return trackingValue === "2s"; // Only 2s (immediate) needs safeguard, 16s is a regular trigger
  };

  const sensorTrackingValue = (actionSettings?.sensorTracking ?? true) ? 1 : 0;
  const durationSeconds = actionSettings?.duration || 4;

  // Only apply safeguard for immediate actions (2s)
  if (!isShortDurationAction(tracking)) {
    // For non-immediate actions, just publish normally
    // For irrigation, include duration parameter and sensor tracking
    if (isIrrigationActuator(actionCard.actuatorType)) {
      return await publishMQTTStatus(
        actionCard.macAddress,
        sensorCard.macAddress,
        thresholds,
        tracking,
        durationSeconds,
        sensorTrackingValue
      );
    }
    return await publishMQTTStatus(
      actionCard.macAddress,
      sensorCard.macAddress,
      thresholds,
      tracking
    );
  }

  // Bounds check only applies when "Use sensor data" is on.
  // Time-only mode (sensor tracking off) intentionally ignores live readings.
  if (actionSettings?.sensorTracking ?? true) {
    const validationError = validateImmediateActionBounds(
      actionCard.actuatorType,
      sensorCard,
      latestSensorData,
      setMqttStatus
    );

    if (validationError) {
      // Leave Immediate selected state; caller resets UI tracking to 1h and shows error modal
      handleSettingChange("tracking", "1h");
      return { result: validationError, trackingReset: true };
    }
  }

  // For immediate actions, handle the safeguard logic
  const actionName = isIrrigationActuator(actionCard.actuatorType) ? 'watering' : 
                    actionCard.actuatorType === 'fan' ? 'fan' : 
                    actionCard.actuatorType === 'humidity' ? 'humidifier' :
                    actionCard.actuatorType === 'camera' ? 'camera' : 'action';
  
  const actionType = tracking === "2s" ? "immediate" : "short duration";
  setMqttStatus(<span style={{color: "lightblue"}}>Sending {actionType} {actionName} request...</span>);
  
  try {
    // First MQTT publish for immediate action
    // For irrigation, include duration parameter and sensor tracking
    let result;
    if (isIrrigationActuator(actionCard.actuatorType)) {
      result = await publishMQTTStatus(
        actionCard.macAddress,
        sensorCard.macAddress,
        thresholds,
        tracking,
        durationSeconds,
        sensorTrackingValue
      );
    } else {
      result = await publishMQTTStatus(
        actionCard.macAddress,
        sensorCard.macAddress,
        thresholds,
        tracking
      );
    }

    if (result === "success") {
      // Update status to indicate action in progress
      setMqttStatus(<span style={{color: "lightgreen"}}>{actionName.charAt(0).toUpperCase() + actionName.slice(1)} in progress...</span>);
      
      // Wait based on the duration parameter, then revert to 1h
      const waitTime = durationSeconds * 1000; // Convert to milliseconds
      await new Promise(resolve => setTimeout(resolve, waitTime));
      
      // Update status to indicate changing back to 1h
      const durationText = `${durationSeconds} seconds`;
      setMqttStatus(<span style={{color: "lightblue"}}>Resetting to 1 hour interval after {durationText}...</span>);
      
      // Second MQTT publish to set back to 1h
      let resetResult;
      if (isIrrigationActuator(actionCard.actuatorType)) {
        resetResult = await publishMQTTStatus(
          actionCard.macAddress,
          sensorCard.macAddress,
          thresholds,
          "1h",
          durationSeconds,
          sensorTrackingValue
        );
      } else {
        resetResult = await publishMQTTStatus(
          actionCard.macAddress,
          sensorCard.macAddress,
          thresholds,
          "1h"
        );
      }
      
      // Also update the UI dropdown to match
      handleSettingChange("tracking", "1h");

      if (resetResult === "success") {
        return { result: resetResult, trackingReset: true };
      }

      // Pump may already have run; schedule revert failed — caller shows error modal.
      return {
        result: `failed: Watering may have run, but resetting the schedule to 1 hour failed: ${formatActionFailureMessage(resetResult)} Please set interval to 1 hour and Apply Changes.`,
        trackingReset: true,
      };
    } else {
      // If the first publish failed, reset tracking to 1h and update UI
      console.warn(`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} ${actionName} failed, resetting to 1h interval`, result);
      handleSettingChange("tracking", "1h");
      return { result: result, trackingReset: true };
    }
  } catch (error) {
    console.error(`Error in ${actionName} safeguard:`, error);
    
    // If we're in immediate mode or short duration mode and there's an error, reset to 1h
    if (isShortDurationAction(tracking)) {
      console.warn(`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} ${actionName} safeguard error, resetting to 1h interval`);
      handleSettingChange("tracking", "1h");
    }
    
    return { result: `failed: error in ${actionName} safeguard`, trackingReset: true };
  }
};

/**
 * Validate sensor readings against bounds for immediate actions
 * @param {string} actuatorType - The type of actuator
 * @param {Object} sensorCard - The sensor card with threshold settings
 * @param {Object} latestSensorData - The latest sensor readings
 * @param {Function} setMqttStatus - Function to set MQTT status display
 * @returns {string|null} - Returns error message or null if validation passes
 */
export const validateImmediateActionBounds = (actuatorType, sensorCard, latestSensorData, setMqttStatus) => {
  if (!latestSensorData) {
    return null; // No validation if no sensor data available
  }

  let sensorValue, lowThreshold, highThreshold, sensorName, unit;

  switch (actuatorType) {
    case 'irrigation':
      sensorValue = latestSensorData.mostRecentDistance;
      lowThreshold = parseFloat(sensorCard.waterLow);
      highThreshold = parseFloat(sensorCard.waterHigh);
      sensorName = 'Water level';
      unit = '"';
      break;
    case 'irrigation2':
      sensorValue = latestSensorData.mostRecentDistance2;
      lowThreshold = parseFloat(sensorCard.water2Low);
      highThreshold = parseFloat(sensorCard.water2High);
      sensorName = 'Water level 2';
      unit = '"';
      break;
    case 'fan':
    case 'humidity':
      sensorValue = latestSensorData.mostRecentHumidity;
      lowThreshold = parseFloat(sensorCard.humidityLow);
      highThreshold = parseFloat(sensorCard.humidityHigh);
      sensorName = 'Humidity';
      unit = '%';
      break;
    case 'camera':
      sensorValue = latestSensorData.mostRecentLight;
      lowThreshold = parseFloat(sensorCard.lightLow);
      highThreshold = parseFloat(sensorCard.lightHigh);
      sensorName = 'Light level';
      unit = 'lux';
      break;
    default:
      return null; // No validation for other actuator types
  }

  // Check if bounds are set (parseFloat of missing values yields NaN)
  if (
    Number.isNaN(lowThreshold) ||
    Number.isNaN(highThreshold) ||
    lowThreshold === null ||
    lowThreshold === undefined ||
    highThreshold === null ||
    highThreshold === undefined
  ) {
    const errorMessage = `${sensorName} bounds not set. Please configure ${sensorName.toLowerCase()} thresholds before sending immediate ${actuatorType} action.`;
    return `failed: ${errorMessage}`;
  }

  // Check if sensor value is within bounds (inclusive)
  if (
    sensorValue === null ||
    sensorValue === undefined ||
    Number.isNaN(Number(sensorValue)) ||
    sensorValue < lowThreshold ||
    sensorValue > highThreshold
  ) {
    const errorMessage = `${sensorName} (${sensorValue != null && !Number.isNaN(Number(sensorValue)) ? Number(sensorValue).toFixed(2) : 'N/A'}${unit}) is outside bounds [${lowThreshold}${unit}, ${highThreshold}${unit}]. Please update bounds if you want to send immediate ${actuatorType} action.`;
    return `failed: ${errorMessage}`;
  }

  return null; // Validation passed
};